// Card "Minha assinatura" do Financeiro (mockup do checkout, tela 3): plano,
// periodicidade, ciclo, próxima cobrança, cartão, renovação, as ações de
// gestão (PATCH /subscriptions/me) e a nota de regras. Atualiza sozinho
// pelos eventos `company-<id>-subscription` / `-payment`.
// Só para admin: as rotas /subscriptions/* respondem 403 aos demais.
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CardMembershipIcon from "@material-ui/icons/CardMembership";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import DateRangeIcon from "@material-ui/icons/DateRange";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import PaymentIcon from "@material-ui/icons/Payment";
import DoneAllIcon from "@material-ui/icons/DoneAll";

import { AuthContext } from "../../context/Auth/AuthContext";
import useSubscription from "../../hooks/useSubscription";
import { openApi } from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { formatBRL } from "../SubscriptionCheckout/checkoutRules";
import Notice from "./Notice";
import CancelDialog from "./CancelDialog";
import ChangePeriodDialog from "./ChangePeriodDialog";
import ChangeCardDialog from "./ChangeCardDialog";
import ChangePlanDialog from "./ChangePlanDialog";
import {
  acoesDisponiveis,
  podeVerificarPagamento,
  brandLabel,
  periodLabel,
  periodicidadeLabel,
  precoNoPeriodo,
  proximaCobranca,
  renovacaoLabel,
  statusLabel,
  statusTone,
  upgradePendente,
} from "./subscriptionCardRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const tone = (color, key) => ({
    color: stateText(theme, color, key),
    backgroundColor: alpha(color, 0.1),
    border: `1px solid ${alpha(color, 0.3)}`,
  });
  return {
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 14,
      padding: "18px 20px",
      marginBottom: 16,
      [theme.breakpoints.down("xs")]: { padding: "16px 14px" },
    },
    head: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      fontFamily: AEROS.fontDisplay,
      fontSize: 15,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& > .MuiSvgIcon-root": { fontSize: 20, color: stateText(theme, "#C08BFF", "purple") },
    },
    pill: {
      marginLeft: "auto",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 99,
      padding: "3px 10px",
      "&::before": {
        content: '""',
        width: 6,
        height: 6,
        borderRadius: "50%",
        backgroundColor: "currentColor",
      },
    },
    ok: tone(AEROS.success, "success"),
    warn: tone(AEROS.warning, "warning"),
    error: tone(AEROS.error, "error"),
    muted: {
      color: theme.palette.text.secondary,
      backgroundColor: isLight ? "rgba(15,20,40,0.04)" : "rgba(255,255,255,0.05)",
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
    },
    kv: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
      gap: "12px 20px",
      marginTop: 14,
      "& > div": { display: "flex", flexDirection: "column", gap: 2, minWidth: 0 },
    },
    k: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      fontWeight: 700,
    },
    v: {
      fontSize: 13.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontVariantNumeric: "tabular-nums",
      overflowWrap: "anywhere",
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
      "& .MuiSvgIcon-root": { fontSize: 16, color: theme.palette.text.secondary },
    },
    sub: { fontSize: 12, fontWeight: 500, color: stateText(theme, AEROS.warning, "warning") },
    vOk: { color: stateText(theme, AEROS.success, "success") },
    acts: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 },
    act: {
      minHeight: 44,
      borderRadius: 10,
      padding: "7px 14px",
      fontSize: 12.5,
      fontWeight: 600,
      textTransform: "none",
      touchAction: "manipulation",
      "& .MuiSvgIcon-root": { fontSize: 16 },
      [theme.breakpoints.down("xs")]: { flex: "1 1 100%" },
    },
    outline: {
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.16)"}`,
      color: theme.palette.text.primary,
    },
    actDanger: {
      color: stateText(theme, AEROS.error, "error"),
      borderColor: alpha(AEROS.error, 0.4),
      "&:hover": { backgroundColor: alpha(AEROS.error, 0.08) },
    },
    fine: {
      fontSize: 11.5,
      lineHeight: 1.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 12,
    },
    notes: { display: "flex", flexDirection: "column", gap: 8, marginTop: 14 },
    center: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
      marginTop: 12,
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
  };
});

// Plano público em { MENSAL, ANUAL|null }.
const precosDoPlano = (plan) =>
  plan
    ? {
        MENSAL: precoNoPeriodo(plan, "MENSAL"),
        ANUAL: precoNoPeriodo(plan, "ANUAL") || null,
      }
    : null;

const SubscriptionCard = ({ onPayInvoice, onSubscribe, onChanged }) => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const { me, getCheckout, reconcile } = useSubscription();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [plans, setPlans] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    try {
      let resumo = await me();
      // Contratação aguardando confirmação: pergunta ao Mercado Pago em
      // silêncio (webhook perdido ou não configurado). Falha não bloqueia o card.
      if (resumo?.subscription?.status === "PENDING") {
        resumo = await reconcile().catch(() => resumo);
      }
      setData(resumo);
      setError(false);
      // Preços do plano vigente (mesmo privado), public key e desconto Pix.
      const info = await getCheckout().catch(() => null);
      setCheckout(info);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [me, getCheckout, reconcile]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // `listPublic: "false"` é o filtro de planos públicos do backend.
    openApi
      .get("/plans/list", { params: { listPublic: "false" } })
      .then(({ data: lista }) => setPlans(Array.isArray(lista) ? lista : []))
      .catch(() => setPlans([]));
  }, []);

  const companyId = user?.companyId;
  useEffect(() => {
    if (!socket || !companyId) return undefined;
    const onEvent = () => {
      load();
      if (onChanged) onChanged();
    };
    const subscriptionEvent = `company-${companyId}-subscription`;
    const paymentEvent = `company-${companyId}-payment`;
    socket.on(subscriptionEvent, onEvent);
    socket.on(paymentEvent, onEvent);
    return () => {
      socket.off(subscriptionEvent, onEvent);
      socket.off(paymentEvent, onEvent);
    };
  }, [socket, companyId, load, onChanged]);

  const subscription = data?.subscription || null;
  const openInvoice = data?.openInvoice || null;

  const currentPlan = useMemo(() => {
    if (!subscription) return null;
    const publico = plans.find((p) => p.id === subscription.planId);
    if (publico) return publico;
    const doCheckout = checkout?.plan;
    if (doCheckout && (!doCheckout.id || doCheckout.id === subscription.planId)) {
      return { ...doCheckout, id: subscription.planId };
    }
    const daEmpresa = user?.company?.plan;
    return daEmpresa && daEmpresa.id === subscription.planId ? daEmpresa : null;
  }, [subscription, plans, checkout, user]);

  // Upgrade aguardando pagamento também grava `pendingPlanId`, mas não é
  // troca agendada: o plano novo só vale quando a fatura UPGRADE for paga.
  const upgrade = upgradePendente(subscription, openInvoice);

  // Downgrade agendado para o fim do ciclo (nunca o upgrade pendente).
  const pendingPlan = useMemo(
    () =>
      subscription?.pendingPlanId && !upgrade
        ? plans.find((p) => p.id === subscription.pendingPlanId) || null
        : null,
    [subscription, plans, upgrade]
  );
  // Visão "agendada" para os modais: sem o pendingPlanId do upgrade.
  const scheduled = useMemo(
    () =>
      subscription && upgrade ? { ...subscription, pendingPlanId: null } : subscription,
    [subscription, upgrade]
  );

  // Preços do plano que vale no próximo ciclo (downgrade agendado ou atual).
  const nextPrices = useMemo(
    () => precosDoPlano(pendingPlan) || checkout?.prices || precosDoPlano(currentPlan),
    [pendingPlan, checkout, currentPlan]
  );
  const currentPrices = useMemo(
    () => checkout?.prices || precosDoPlano(currentPlan),
    [checkout, currentPlan]
  );
  const currentPrice = Number(currentPrices?.[subscription?.period || "MENSAL"]) || 0;

  const handleDone = useCallback(
    (message, tone = "success") => {
      setDialog(null);
      if (message) {
        if (tone === "warn") toast.warn(message);
        else if (tone === "info") toast.info(message);
        else toast.success(message);
      }
      load();
      if (onChanged) onChanged();
    },
    [load, onChanged]
  );
  const closeDialog = useCallback(() => setDialog(null), []);

  if (loading) {
    return (
      <section className={classes.card} aria-busy="true">
        <div className={classes.head}>
          <CardMembershipIcon aria-hidden="true" />
          {i18n.t("subscription.card.title")}
        </div>
        <div className={classes.center}>
          <CircularProgress size={20} />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={classes.card}>
        <div className={classes.head}>
          <CardMembershipIcon aria-hidden="true" />
          {i18n.t("subscription.card.title")}
        </div>
        <div className={classes.center}>
          {i18n.t("subscription.card.loadError")}
          <Button
            className={`${classes.act} ${classes.outline}`}
            onClick={() => {
              setLoading(true);
              load();
            }}
          >
            {i18n.t("subscription.checkout.retry")}
          </Button>
        </div>
      </section>
    );
  }

  if (!subscription) {
    return (
      <section className={classes.card}>
        <div className={classes.head}>
          <CardMembershipIcon aria-hidden="true" />
          {i18n.t("subscription.card.title")}
        </div>
        <div className={classes.center}>
          <span style={{ flex: "1 1 240px" }}>{i18n.t("subscription.card.empty")}</span>
          {onSubscribe && (
            <Button
              variant="contained"
              color="primary"
              className={classes.act}
              onClick={() => onSubscribe()}
            >
              {i18n.t("subscription.card.actions.subscribe")}
            </Button>
          )}
        </div>
      </section>
    );
  }

  const acoes = acoesDisponiveis(subscription, openInvoice);
  const fmt = (d) => (d ? moment(d).format("DD/MM/YYYY") : "—");
  const cycleEnd = fmt(subscription.cycleEnd);
  const proxima = proximaCobranca({
    subscription,
    openInvoice,
    prices: nextPrices,
    pixDiscountPercent: checkout?.pixDiscountPercent,
  });
  const ehCartao = subscription.paymentMethod && subscription.paymentMethod !== "PIX";
  const cardLabel =
    ehCartao && subscription.cardLast4
      ? i18n.t("subscription.card.cardMasked", {
          brand: brandLabel(subscription.cardBrand) || i18n.t("subscription.card.cardOnFile"),
          last4: subscription.cardLast4,
        })
      : null;
  const cancelada = subscription.status === "CANCELED" || !!subscription.canceledAt;
  const periodoVigente = subscription.pendingPeriod || subscription.period || "MENSAL";
  const periodoAlvo = periodoVigente === "ANUAL" ? "MENSAL" : "ANUAL";
  // Plano sem preço anual: não há para onde trocar.
  const podeTrocarPeriodo =
    acoes.includes("changePeriod") && !(nextPrices && !nextPrices[periodoAlvo] && !subscription.pendingPeriod);
  const upgradeAberto = upgrade;
  const faturaParaPagar = openInvoice && !cancelada;

  const verificar = podeVerificarPagamento(subscription, openInvoice);

  // "Já paguei": consulta o Mercado Pago e aplica o pagamento aprovado. Se a
  // assinatura ativou, recarrega como o checkout faz — a rota de licença
  // vencida depende do `dueDate` do AuthContext, que só muda no reload.
  const handleVerificar = async () => {
    setChecking(true);
    try {
      const resumo = await reconcile();
      const quitou =
        resumo?.reconciled > 0 &&
        (resumo?.subscription?.status === "ACTIVE" ||
          resumo?.openInvoice?.id !== openInvoice?.id);
      setData(resumo);
      if (quitou) {
        toast.success(i18n.t("subscription.checkout.paid"));
        if (onChanged) onChanged();
        window.location.assign("/");
      } else {
        toast.info(i18n.t("subscription.checkout.stillPending"));
      }
    } catch (err) {
      toast.error(i18n.t("subscription.card.loadError"));
    } finally {
      setChecking(false);
    }
  };

  const payOpenInvoice = (invoice) =>
    onPayInvoice &&
    onPayInvoice({
      period: subscription.period,
      dueDate: moment().format("YYYY-MM-DD"),
      ...invoice,
      status: "open",
      subscriptionId: subscription.id,
    });

  return (
    <section className={classes.card} aria-labelledby="subscription-card-title">
      <div className={classes.head}>
        <CardMembershipIcon aria-hidden="true" />
        <span id="subscription-card-title">{i18n.t("subscription.card.title")}</span>
        <span className={`${classes.pill} ${classes[statusTone(subscription)]}`}>
          {statusLabel(subscription)}
        </span>
      </div>

      <div className={classes.kv}>
        <div>
          <span className={classes.k}>{i18n.t("subscription.card.plan")}</span>
          <span className={classes.v}>{currentPlan?.name || "—"}</span>
          {pendingPlan && (
            <span className={classes.sub}>
              {i18n.t("subscription.card.pendingPlan", {
                plan: pendingPlan.name,
                date: cycleEnd,
              })}
            </span>
          )}
        </div>
        <div>
          <span className={classes.k}>{i18n.t("subscription.card.periodicity")}</span>
          <span className={classes.v}>{periodicidadeLabel(subscription)}</span>
          {subscription.pendingPeriod && subscription.pendingPeriod !== subscription.period && (
            <span className={classes.sub}>
              {i18n.t("subscription.card.pendingPeriod", {
                period: periodLabel(subscription.pendingPeriod).toLowerCase(),
                date: cycleEnd,
              })}
            </span>
          )}
        </div>
        <div>
          <span className={classes.k}>
            {i18n.t(cancelada ? "subscription.card.accessUntil" : "subscription.card.cycle")}
          </span>
          <span className={classes.v}>
            {cancelada
              ? cycleEnd
              : i18n.t("subscription.card.cycleRange", {
                  start: fmt(subscription.cycleStart),
                  end: cycleEnd,
                })}
          </span>
        </div>
        {proxima && (
          <div>
            <span className={classes.k}>{i18n.t("subscription.card.nextCharge")}</span>
            <span className={classes.v}>
              {i18n.t("subscription.card.nextChargeValue", {
                date: proxima.date,
                value: formatBRL(proxima.value),
              })}
            </span>
          </div>
        )}
        <div>
          <span className={classes.k}>{i18n.t("subscription.card.paymentMethod")}</span>
          <span className={classes.v}>
            {ehCartao ? <CreditCardIcon aria-hidden="true" /> : null}
            {cardLabel ||
              (subscription.paymentMethod === "PIX"
                ? i18n.t("subscription.card.methodPix")
                : ehCartao
                ? i18n.t("subscription.card.cardOnFile")
                : "—")}
          </span>
        </div>
        <div>
          <span className={classes.k}>{i18n.t("subscription.card.renewal.label")}</span>
          <span
            className={`${classes.v} ${
              subscription.paymentMethod === "CARD_RECURRING" && !cancelada ? classes.vOk : ""
            }`}
          >
            {renovacaoLabel(subscription)}
          </span>
        </div>
      </div>

      {(subscription.status === "PENDING" ||
        subscription.status === "PAST_DUE" ||
        upgradeAberto ||
        cancelada) && (
        <div className={classes.notes}>
          {cancelada && (
            <Notice>{i18n.t("subscription.card.canceledNote", { date: cycleEnd })}</Notice>
          )}
          {subscription.status === "PENDING" && (
            <Notice tone="warn">{i18n.t("subscription.card.pendingNote")}</Notice>
          )}
          {subscription.status === "PAST_DUE" && (
            <Notice tone="error">{i18n.t("subscription.card.pastDueNote")}</Notice>
          )}
          {upgradeAberto && !cancelada && (
            <Notice tone="warn">
              {i18n.t("subscription.card.upgradePendingNote", {
                value: formatBRL(openInvoice.value),
              })}
            </Notice>
          )}
        </div>
      )}

      {(acoes.length > 0 || faturaParaPagar || verificar) && (
        <div className={classes.acts}>
          {verificar && (
            <Button
              variant="contained"
              color="primary"
              className={classes.act}
              startIcon={checking ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
              disabled={checking}
              onClick={handleVerificar}
            >
              {i18n.t("subscription.card.actions.checkPayment")}
            </Button>
          )}
          {faturaParaPagar && onPayInvoice && (
            <Button
              variant="contained"
              color="primary"
              className={classes.act}
              startIcon={<PaymentIcon />}
              onClick={() => payOpenInvoice(openInvoice)}
            >
              {i18n.t("subscription.card.actions.payInvoice", {
                value: formatBRL(openInvoice.value),
              })}
            </Button>
          )}
          {acoes.includes("changePlan") && (
            <Button
              className={`${classes.act} ${classes.outline}`}
              startIcon={<SwapHorizIcon />}
              onClick={() => setDialog("changePlan")}
            >
              {i18n.t("subscription.card.actions.changePlan")}
            </Button>
          )}
          {podeTrocarPeriodo && (
            <Button
              className={`${classes.act} ${classes.outline}`}
              startIcon={<DateRangeIcon />}
              onClick={() => setDialog("changePeriod")}
            >
              {i18n.t(
                periodoAlvo === "ANUAL"
                  ? "subscription.card.actions.toYearly"
                  : "subscription.card.actions.toMonthly"
              )}
            </Button>
          )}
          {acoes.includes("changeCard") && (
            <Button
              className={`${classes.act} ${classes.outline}`}
              startIcon={<CreditCardIcon />}
              onClick={() => setDialog("changeCard")}
            >
              {i18n.t("subscription.card.actions.changeCard")}
            </Button>
          )}
          {acoes.includes("cancel") && (
            <Button
              className={`${classes.act} ${classes.outline} ${classes.actDanger}`}
              startIcon={<HighlightOffIcon />}
              onClick={() => setDialog("cancel")}
            >
              {i18n.t("subscription.card.actions.cancel")}
            </Button>
          )}
        </div>
      )}

      {acoes.length > 0 && (
        <div className={classes.fine}>
          {i18n.t("subscription.card.rulesNote", { date: cycleEnd })}
        </div>
      )}

      <CancelDialog
        open={dialog === "cancel"}
        onClose={closeDialog}
        subscription={subscription}
        planName={currentPlan?.name}
        onDone={handleDone}
      />
      <ChangePeriodDialog
        open={dialog === "changePeriod"}
        onClose={closeDialog}
        subscription={subscription}
        prices={nextPrices}
        planName={(pendingPlan || currentPlan)?.name}
        onDone={handleDone}
      />
      <ChangeCardDialog
        open={dialog === "changeCard"}
        onClose={closeDialog}
        publicKey={checkout?.publicKey}
        amount={currentPrice}
        onDone={handleDone}
      />
      <ChangePlanDialog
        open={dialog === "changePlan"}
        onClose={closeDialog}
        subscription={scheduled}
        plans={plans}
        currentPlan={currentPlan}
        currentPrice={currentPrice}
        cardLabel={cardLabel}
        onDone={handleDone}
        onUpgradeOpen={payOpenInvoice}
      />
    </section>
  );
};

export default SubscriptionCard;
